import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Users, LogOut } from 'lucide-react';
import { useAuth } from '../../context/UserAuthContext';

/**
 * Counterfind-only dashboard header.
 *
 * Renders a search bar (with mock results dropdown) on the left and a profile
 * icon with a Profile / Settings / Help & Support / Team Admin / Sign Out
 * dropdown on the right. Drop it at the top of each role dashboard so the
 * top-right chrome from the original Managing Partner dashboard is present
 * across Client / Executive / Sales / Operations / Review.
 */
const CounterfindDashboardHeader = ({ roleLabel = 'Counterfind' }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showUserMenu) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showUserMenu]);

  const displayName = user?.name || user?.email?.split('@')[0] || 'CounterFind User';
  const displayEmail = user?.email || 'counterfind@operatr.com';
  const displayRole = user?.role || roleLabel;

  return (
    <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="counterfind-dashboard-header">
      {/* Spacer so the right-side controls hug the right edge */}
      <div className="flex-1" />

      <div className="flex items-center gap-3 w-full sm:w-auto">
        {/* Search */}
        <div className="relative flex-1 sm:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents, tasks, events"
            data-testid="counterfind-header-search"
            className="bg-black border border-gray-400 rounded-lg pl-10 pr-4 py-2 w-full sm:w-80 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchQuery && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-black border border-gray-400 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
              <div className="p-3 text-sm text-gray-400">
                Search results for "{searchQuery}"
              </div>
              <div className="border-t border-gray-800">
                <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors">
                  <div className="font-semibold text-sm">Nike — Brand Authorisation</div>
                  <div className="text-xs text-gray-400 mt-1">Document • Updated 2 days ago</div>
                </button>
                <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors">
                  <div className="font-semibold text-sm">Q1 Takedown Performance Report</div>
                  <div className="text-xs text-gray-400 mt-1">Report • Updated 1 week ago</div>
                </button>
                <button className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors">
                  <div className="font-semibold text-sm">Apex Electronics — Counterfeit Audit</div>
                  <div className="text-xs text-gray-400 mt-1">Task • Due Tomorrow</div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Profile button + dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowUserMenu((s) => !s)}
            data-testid="counterfind-header-profile-btn"
            aria-label="Open profile menu"
            className="bg-black border border-gray-400 rounded-lg p-2 hover:bg-gray-900 transition-colors"
          >
            <User size={20} />
          </button>

          {showUserMenu && (
            <div
              className="absolute right-0 top-full mt-2 w-64 bg-black border border-gray-400 rounded-lg shadow-xl z-50"
              data-testid="counterfind-header-profile-menu"
            >
              <div className="p-4 border-b border-gray-800">
                <div className="font-bold text-sm">{displayName}</div>
                <div className="text-xs text-gray-400 mt-1 capitalize">{displayRole}</div>
                <div className="text-xs text-gray-400 truncate">{displayEmail}</div>
              </div>
              <div>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                  data-testid="counterfind-header-my-profile"
                  className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm"
                >
                  My Profile
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                  data-testid="counterfind-header-settings"
                  className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm"
                >
                  Settings
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/help-support'); }}
                  data-testid="counterfind-header-help"
                  className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm"
                >
                  Help & Support
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/team-admin'); }}
                  data-testid="counterfind-header-team-admin"
                  className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm flex items-center gap-2 text-blue-400"
                >
                  <Users size={16} />
                  Team Admin
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    if (logout) logout();
                    navigate('/auth');
                  }}
                  data-testid="counterfind-header-sign-out"
                  className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors text-sm border-t border-gray-800 text-red-400 flex items-center gap-2"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CounterfindDashboardHeader;
